import { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, isBefore, addMonths, subMonths, isSameMonth, isAfter } from 'date-fns';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { getEvents, Event } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export default function MembersUpcomingEvents() {
  const [date, setDate] = useState<Date>(new Date());
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  
  const currentMonth = new Date();
  currentMonth.setDate(1); // Set to first day of current month
  
  useEffect(() => {
    fetchEvents();
  }, [date]);
  
  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      
      const startDate = startOfMonth(date);
      const endDate = endOfMonth(date);
      
      console.log('Fetching events from', startDate, 'to', endDate);
      
      // Use the getEvents helper function instead of direct query
      const { data, error } = await getEvents(startDate, endDate);
      
      if (error) {
        console.error('Error fetching events:', error);
        toast.error('Failed to load events');
        throw error;
      }
      
      console.log('Fetched events:', data);
      
      // Filter out expired events
      const now = new Date();
      const upcomingEvents = (data || []).filter(event => {
        const eventEndTime = new Date(event.end_time);
        return isAfter(eventEndTime, now);
      });
      
      setEvents(upcomingEvents);
      if (upcomingEvents.length > 0) {
        setSelectedEvent(upcomingEvents[0]);
      } else {
        setSelectedEvent(null);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('There was a problem loading events');
    } finally {
      setIsLoading(false);
    }
  };
  
  const formatEventTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'h:mm a');
    } catch (e) {
      return 'Invalid time';
    }
  };
  
  const formatEventDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'EEEE, MMMM d, yyyy');
    } catch (e) {
      return 'Invalid date';
    }
  };
  
  const getEventTypeLabel = (event: Event) => {
    if (event.custom_type) return event.custom_type;
    return event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1);
  };

  const goToPreviousMonth = () => {
    // Only go to previous month if it's not before the current month
    const previousMonth = subMonths(date, 1);
    if (!isBefore(startOfMonth(previousMonth), startOfMonth(currentMonth))) {
      setDate(previousMonth);
    }
  };

  const goToNextMonth = () => {
    setDate(addMonths(date, 1));
  };

  const isPreviousMonthDisabled = isBefore(
    startOfMonth(subMonths(date, 1)),
    startOfMonth(currentMonth)
  ) || isSameMonth(date, currentMonth);

  return (
    <div className="p-6">
     <h2 className="text-2xl font-semibold mb-6 text-taekwondo-red">Upcoming Events</h2>
      
      <div className="flex flex-col md:flex-row gap-8">
        {/* Left Column - Month Navigation and Event List */}
        <div className="w-full md:w-1/2 space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col space-y-4">
                {/* Custom Month Navigation */}
                <div className="flex items-center justify-between mb-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={goToPreviousMonth}
                    disabled={isPreviousMonthDisabled}
                    className={isPreviousMonthDisabled ? "opacity-50 cursor-not-allowed" : ""}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <h3 className="text-lg font-medium">
                    {format(date, 'MMMM yyyy')}
                  </h3>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={goToNextMonth}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <p>Loading events...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="bg-gray-50 rounded-md p-6 text-center">
              <p className="text-gray-500">No upcoming events scheduled for {format(date, 'MMMM yyyy')}.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <h3 className="text-sm font-medium mb-2">
                Events for {format(date, 'MMMM yyyy')}
              </h3>
              <ul className="space-y-2">
                {events.map((event) => (
                  <li key={event.id}>
                    <Button
                      variant="ghost"
                      className={cn(
                        "w-full justify-start text-left px-3 py-2 h-auto",
                        selectedEvent?.id === event.id ? "bg-gray-100" : ""
                      )}
                      onClick={() => setSelectedEvent(event)}
                    >
                      <div className="flex w-full items-center justify-between gap-2 min-w-0">
                        <p className="font-medium text-taekwondo-red whitespace-normal break-words flex-1">{event.title}</p>
                        <p className="text-xs text-gray-500 flex-shrink-0 whitespace-nowrap">
                          {format(new Date(event.start_time), 'MMM d')} · {formatEventTime(event.start_time)}
                        </p>
                      </div>
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        {/* Right Column - Event Details */}
        <div className="w-full md:w-1/2">
          {selectedEvent ? (
            <div className="bg-gray-50 rounded-md p-6 border">
              <h3 className="text-xl font-semibold mb-2 text-taekwondo-red">{selectedEvent.title}</h3>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Event Type</p>
                  <p>{getEventTypeLabel(selectedEvent)}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500">Date</p>
                  <p>{formatEventDate(selectedEvent.start_time)}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500">Time</p>
                  <p>{formatEventTime(selectedEvent.start_time)} - {formatEventTime(selectedEvent.end_time)}</p>
                </div>
                
                {selectedEvent.comments && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">Details</p>
                    <p>{selectedEvent.comments}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-md p-6 text-center h-full flex items-center justify-center">
              <p className="text-gray-500">
                {isLoading ? "Loading event details..." : "Select an event to view details"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
